import { FC, useEffect, useState } from 'react';
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import { useWallet } from '@solana/wallet-adapter-react';
import { toast } from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';

interface ExternalWalletLoginProps {
  variant?: 'primary' | 'muted';
}

const ExternalWalletLogin: FC<ExternalWalletLoginProps> = ({ variant = 'primary' }) => {
  const { connecting, connected, connect, disconnect, publicKey, wallet } = useWallet();
  const [shouldAutoConnect, setShouldAutoConnect] = useState(false);  // This is used to prevent the wallet from auto-connecting when the user is not logged in esp important on mobile
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // Monitor connection state
  useEffect(() => {
    if (connected && publicKey) {
      // The SolanaWalletConnector will handle creating our adapter
      // and the LoginPage will handle the redirect
      console.log('[ExternalWalletLogin] Wallet connected:', publicKey.toString());
    }
  }, [connected, publicKey]);

  // Handle wallet selection and connection
  useEffect(() => {
    if (wallet && !connected && !connecting && shouldAutoConnect) {
      console.log('[ExternalWalletLogin] Wallet selected, initiating connection');
      connect().catch((error) => {
        console.error('Error connecting wallet:', error);
        toast.error('Failed to connect wallet');
      });
    }
  }, [wallet, connected, connecting, connect, shouldAutoConnect]);

  const handleWalletSelect = async () => {
    try {
      setShouldAutoConnect(true);
      console.log('[ExternalWalletLogin] Opening wallet selector');
      const button = document.querySelector('.wallet-adapter-button-trigger') as HTMLElement;
      if (button) {
        button.click();
      }
    } catch (error) {
      console.error('Error selecting wallet:', error);
      toast.error('Failed to open wallet selector');
    }
  };

  const handleDisconnect = async () => {
    try {
      setShouldAutoConnect(false);
      await disconnect();
      toast.success('Wallet disconnected');
    } catch (error) {
      console.error('Error disconnecting wallet:', error);
      toast.error('Failed to disconnect wallet');
    }
  };

  // Button styling
  const mutedBgClass = isDarkMode ? 'bg-gray-700' : 'bg-gray-100';
  const mutedTextClass = isDarkMode ? 'text-gray-300' : 'text-gray-500';

  const primaryButtonClasses = "w-full py-4 px-6 bg-[#00CC6A] rounded-xl text-[#2B2B2B] font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50";
  const mutedButtonClasses = `w-full py-4 px-6 ${mutedBgClass} rounded-xl ${mutedTextClass} hover:opacity-90 transition-opacity font-semibold font-inter flex items-center justify-center gap-2 disabled:opacity-50`;

  const buttonClasses = variant === 'muted' ? mutedButtonClasses : primaryButtonClasses;
  const buttonText = variant === 'muted' ? 'Connect with Wallet (advanced)' : 'Select Wallet';

  return (
    <div className="w-full [&_.wallet-adapter-dropdown]:w-full">
      <button
        onClick={handleWalletSelect}
        disabled={connecting}
        className={buttonClasses}
      >
        {connecting ? (
          <>
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Connecting...
          </>
        ) : (
          buttonText
        )}
      </button>
      {connecting && (
        <button
          onClick={handleDisconnect}
          className="mt-2 w-full py-2 px-4 bg-red-500 rounded-xl text-white font-semibold hover:opacity-90 transition-opacity"
        >
          Cancel Connection
        </button>
      )}
      <div className="hidden">
        <WalletMultiButton className="wallet-adapter-button wallet-adapter-button-trigger" />
      </div>
    </div>
  );
};

export default ExternalWalletLogin; 